import {vnTemplate} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate";
import {DragAndDropHandler} from "../../utils/DragAndDropHandler";
import {filterAutocomplete_template} from "./filterAutocomplete_template";

declare var ko: any;

export interface pinnedDiscountItem {
    id:       number;
    name:     string;
    slug:     string;
    position: number;
}

/** Manual link row for Popular Categories (stored as label + url). */
export interface popularCategoryLinkItem {
    label:    string;
    url:      string;
    position: number;
}

/** Map from API listType to view model section key. Used for set/get and filter routing. */
const SECTION_BY_LIST_TYPE: { [listType: string]: string } = {
    featured_discounts:  'featuredDiscounts',
    popular_categories:  'popularCategories',
};

export class discountLists_template extends vnTemplate {

    public filterFeaturedDiscounts: filterAutocomplete_template;

    // ==========================================
    // Template
    // ==========================================

    protected getTemplateHtml(): string {

        return /*html*/`
            <div class="w-form">
                <div class="form-header">
                    <input type="hidden">
                    <h3 class="form-title">Discount Homepage</h3>
                </div>

                <div class="form-body">

                    <!-- Featured Discounts -->
                    <h3 class="form-sub-title">Featured Discounts</h3>
                    <div class="form-group">
                        <p class="form-text">Hero section &mdash; discounts shown prominently on homepage</p>
                        ${this.buildPinnedSectionHtml('featuredDiscounts')}
                    </div>

                    <!-- Popular Categories (manual label + URL) -->
                    <h3 class="form-sub-title">Popular Categories</h3>
                    <div class="form-group">
                        <p class="form-text">Add links with a label and URL; drag entries to reorder.</p>
                        ${this.buildPopularCategoriesSectionHtml()}
                    </div>

                    <div class="right">
                        <button class="btn highlight" type="button" data-bind="event: { click: onSaveHomepage }">Save Homepage</button>
                    </div>

                </div>
            </div>
        `;
    }

    /**
     * Build pinned items HTML for discount-driven sections (autocomplete).
     */
    private buildPinnedSectionHtml(sectionKey: string): string {

        const filter = this.getFilterBySectionKey(sectionKey);
        const itemsProp = sectionKey + 'Items';
        const onClickAdd = 'onClickAdd_' + sectionKey;
        const onClickDelete = 'onClickDelete_' + sectionKey;

        return /*html*/`
                <div class="w-pinned mlt-discount-pinned">
                    <div class="pinned-form">
                        ${filter.getHtmlBuild()}
                        <div class="right">
                            <button class="btn secondary" type="button" data-bind="enable: ${itemsProp}().length < 25, event:{click: ${onClickAdd} }, text: 'Add to Position ' + (${itemsProp}().length + 1)"></button>
                        </div>
                    </div>
                    <div class="form-body" data-bind="foreach: ${itemsProp}, childrenComplete : handleDragAndDrop($element, ${itemsProp} ,'.entry')">
                       <div class="pinned-form article-pinned entry">
                             <div class="pinned-drag icon i-drag">
                                <span class="pinned-position" data-bind="text: ($index() + 1)"></span>
                             </div>
                             <div class="w-btn">
                                <button class="btn border only-icon icon border highlight i-delete" type="button" data-bind="event: { click: $parent.${onClickDelete} }"><span class="hidden-text">Delete</span></button>
                             </div>
                             <div class="w-title">
                                <span class="pinned-title" data-bind="text: name"></span>
                                <div>
                                    <span class="pinned-info">Slug: <span data-bind="text: slug"></span></span>
                                </div>
                             </div>
                       </div>
                    </div>
                </div>
                <div data-bind="visible: ${itemsProp}().length == 0">
                     <p class="form-text">There are no items in this list.</p>
                </div>`;
    }

    private buildPopularCategoriesSectionHtml(): string {

        return /*html*/`
                <div class="w-pinned mlt-discount-pinned">
                    <div class="pinned-form">
                        <div class="form-group">
                            <label class="form-label">Label</label>
                            <input type="text" class="input-text" data-bind="value: popularCategoriesDraftLabel, attr: { placeholder: 'Link label' }" />
                        </div>
                        <div class="form-group">
                            <label class="form-label">URL</label>
                            <input type="text" class="input-text" data-bind="value: popularCategoriesDraftUrl, attr: { placeholder: 'https://... or /discounts/categories/example/' }" />
                        </div>
                        <div class="right">
                            <button class="btn secondary" type="button" data-bind="enable: popularCategoriesItems().length < 25, event: { click: onClickAdd_popularCategories }, text: 'Add to Position ' + (popularCategoriesItems().length + 1)"></button>
                        </div>
                    </div>
                    <div class="form-body" data-bind="foreach: popularCategoriesItems, childrenComplete : handleDragAndDrop($element, popularCategoriesItems ,'.entry')">
                       <div class="pinned-form article-pinned entry">
                             <div class="pinned-drag icon i-drag">
                                <span class="pinned-position" data-bind="text: ($index() + 1)"></span>
                             </div>
                             <div class="w-btn">
                                <button class="btn border only-icon icon border highlight i-delete" type="button" data-bind="event: { click: $parent.onClickDelete_popularCategories }"><span class="hidden-text">Delete</span></button>
                             </div>
                             <div class="w-title">
                                <span class="pinned-title" data-bind="text: label"></span>
                                <div>
                                    <span class="pinned-info">URL: <span data-bind="text: url"></span></span>
                                </div>
                             </div>
                       </div>
                    </div>
                </div>
                <div data-bind="visible: popularCategoriesItems().length == 0">
                     <p class="form-text">There are no items in this list.</p>
                </div>`;
    }

    private getFilterBySectionKey(sectionKey: string): filterAutocomplete_template {

        switch (sectionKey) {
            case 'featuredDiscounts':  return this.filterFeaturedDiscounts;
            default: throw new Error('Unknown section key: ' + sectionKey);
        }
    }

    protected initPartial(): void {

        this.filterFeaturedDiscounts = this.createItemFilter('filter_featuredDiscounts', 'Discount');
    }

    private createItemFilter(filterId: string, filterLabel: string): filterAutocomplete_template {

        const filter = new filterAutocomplete_template(
            filterId,
            this.getTemplateViewModel(),
            this.caller
        );
        filter.setAddNewOptionEnabled(false);
        filter.setNumberOfChars(1);
        filter.setHasDisplayNameComplement(true);
        filter.setPlaceholderAndLabel(filterLabel);
        return filter;
    }

    protected getTemplateName(): string {
        return "discountLists_template";
    }

    protected getMainElementType(): string {
        return "div";
    }

    protected getMainElementClasses(): string[] {
        return ["ww-form", "config-homepage"];
    }

    // ==========================================
    // View model — explicit properties like configHomePage
    // ==========================================

    protected buildViewModel(): any {

        let self = this;
        return {
            featuredDiscountsItems:  ko.observableArray([]),
            popularCategoriesItems:  ko.observableArray([]),
            popularCategoriesDraftLabel: ko.observable(''),
            popularCategoriesDraftUrl:   ko.observable(''),

            onClickAdd_featuredDiscounts:    function () { self.onClickAddFeaturedDiscount(); },
            onClickDelete_featuredDiscounts: function (data) { self.onClickDeleteFeaturedDiscount(data); },

            onClickAdd_popularCategories:    function () { self.onAddPopularCategory(); },
            onClickDelete_popularCategories: function (data) { self.onDeletePopularCategory(data); },

            onSaveHomepage: function () { self.caller.saveHomepage(); },

            handleDragAndDrop: function (element, currentObservableArray, childrenSelector) {
                DragAndDropHandler.handleDragAndDrop(element, currentObservableArray, childrenSelector);
            }
        };
    }

    // ==========================================
    // Item management — Featured Discounts
    // ==========================================

    protected onClickAddFeaturedDiscount(): void {

        const filter = this.filterFeaturedDiscounts;
        const selected = filter.getOptionObj();

        if (!selected) {
            this.caller.triggerNotification('Please select an item first!');
            return;
        }

        const vm = this.getTemplateViewModel();
        const itemsObs = vm.featuredDiscountsItems;

        if (itemsObs().length >= 25) {
            this.caller.triggerNotification('Maximum number reached. Please delete one to add new!');
            return;
        }

        let item: pinnedDiscountItem = {
            'id':       selected.id,
            'name':     selected.name,
            'slug':     selected.slug,
            'position': itemsObs().length + 1
        };

        let items = itemsObs();
        let index = items.findIndex(i => i.id == item.id && i.slug == item.slug);

        if (index > -1) {
            this.caller.triggerNotification('Item already in list!');
        } else {
            items.push(item);
            itemsObs([]);
            itemsObs(items);
            filter.clearInputNoCallback();
        }
    }

    protected onClickDeleteFeaturedDiscount(data: any): void {

        const vm = this.getTemplateViewModel();
        const itemsObs = vm.featuredDiscountsItems;
        let items = itemsObs();
        let index = items.findIndex(i => i.id == data.id && i.slug == data.slug);
        if (index > -1) {
            items.splice(index, 1);
        }
        itemsObs([]);
        itemsObs(items);
    }

    // ==========================================
    // Item management — Popular Categories (label + URL)
    // ==========================================

    protected onAddPopularCategory(): void {

        const vm = this.getTemplateViewModel();
        const label = (vm.popularCategoriesDraftLabel() || '').trim();
        const urlRaw = (vm.popularCategoriesDraftUrl() || '').trim();

        if (!label || !urlRaw) {
            this.caller.triggerNotification('Please enter both a label and a URL.');
            return;
        }

        const itemsObs = vm.popularCategoriesItems;
        if (itemsObs().length >= 25) {
            this.caller.triggerNotification('Maximum number reached. Please delete one to add new!');
            return;
        }

        const urlNorm = this.normalizePopularCategoryUrl(urlRaw);
        const dup = itemsObs().some(
            (i: popularCategoryLinkItem) => this.normalizePopularCategoryUrl(i.url) === urlNorm
        );
        if (dup) {
            this.caller.triggerNotification('That URL is already in the list.');
            return;
        }

        const item: popularCategoryLinkItem = {
            label,
            url: urlRaw,
            position: itemsObs().length + 1
        };
        let items = itemsObs();
        items.push(item);
        itemsObs([]);
        itemsObs(items);
        vm.popularCategoriesDraftLabel('');
        vm.popularCategoriesDraftUrl('');
    }

    protected onDeletePopularCategory(data: popularCategoryLinkItem): void {

        const vm = this.getTemplateViewModel();
        const itemsObs = vm.popularCategoriesItems;
        let items = itemsObs();
        let index = items.findIndex(
            i => i.url === data.url && i.label === data.label
        );
        if (index > -1) {
            items.splice(index, 1);
        }
        itemsObs([]);
        itemsObs(items);
    }

    protected normalizePopularCategoryUrl(url: string): string {
        return (url || '').trim().toLowerCase();
    }

    protected updateItemPositions(sectionKey: string): void {

        const vm = this.getTemplateViewModel();
        const itemsObs = vm[sectionKey + 'Items'];
        let items = itemsObs();
        for (let i = 0; i < items.length; i++) {
            items[i].position = i + 1;
        }
        itemsObs([]);
        itemsObs(items);
    }

    // ==========================================
    // Setters — loading existing curated data
    // ==========================================

    public setItemSectionData(listType: string, items: any[]): void {

        const sectionKey = SECTION_BY_LIST_TYPE[listType];
        if (sectionKey === 'popularCategories') {
            this.getTemplateViewModel()[sectionKey + 'Items'](this.normalizePopularCategoryItemsFromApi(items));
            return;
        }
        if (sectionKey) {
            this.getTemplateViewModel()[sectionKey + 'Items'](items);
        }
    }

    /**
     * Maps API rows to { label, url, position }. Supports new shape and legacy category picks { id, name, slug }.
     */
    protected normalizePopularCategoryItemsFromApi(items: any[]): popularCategoryLinkItem[] {

        if (!Array.isArray(items)) {
            return [];
        }
        return items.map((item, idx) => {
            let label = '';
            if (item.label != null && String(item.label).trim() !== '') {
                label = String(item.label).trim();
            } else if (item.name != null) {
                label = String(item.name).trim();
            }
            let url = '';
            if (item.url != null && String(item.url).trim() !== '') {
                url = String(item.url).trim();
            } else if (item.slug != null && String(item.slug).trim() !== '') {
                const s = String(item.slug).replace(/^\/+|\/+$/g, '');
                url = s ? '/' + s + '/' : '';
            }
            return {
                label,
                url,
                position: item.position != null ? Number(item.position) : idx + 1
            };
        });
    }

    // ==========================================
    // Getters — extracting data for save
    // ==========================================

    public getListItems(listType: string): any[] {

        if (listType === 'popular_categories') {
            this.updateItemPositions('popularCategories');
            const rows = this.getTemplateViewModel().popularCategoriesItems() as popularCategoryLinkItem[];
            return rows.map((r, i) => ({
                label:    r.label,
                url:      r.url,
                position: r.position != null ? r.position : i + 1
            }));
        }

        const sectionKey = SECTION_BY_LIST_TYPE[listType];
        if (sectionKey) {
            this.updateItemPositions(sectionKey);
            return this.getTemplateViewModel()[sectionKey + 'Items']();
        }
        return [];
    }

    public getAllListData(): { [listType: string]: any[] } {

        const data: { [listType: string]: any[] } = {};
        const listTypes = Object.keys(SECTION_BY_LIST_TYPE);
        for (let i = 0; i < listTypes.length; i++) {
            const listType   = listTypes[i];
            data[listType] = this.getListItems(listType);
        }
        return data;
    }

    // ==========================================
    // Autocomplete result routing
    // ==========================================

    public setItemFilterOptions(idElement: string, options: any[]): void {

        if (idElement === 'filter_featuredDiscounts') {
            this.filterFeaturedDiscounts.setOptions(options);
        }
    }
}



// WEBPACK FOOTER //
// ./js/components/templates/discountLists_template.ts